import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useSnackbar } from 'notistack';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
// Components
import { Card, Table, SMBSelect } from '../../../../components';
import { Close, DeleteForever, Add, SupervisedUserCircle } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Grid,
  MenuItem,
  Typography,
  Divider,
  TextField,
  ButtonGroup,
  Alert,
  AlertTitle,
  Box
} from '@mui/material';
import { IAvailableNeighborhoodsWarrantyUser, IDropdownResponse } from '../../../../models';
import {
  getAvailableWarrantyUsers,
  getWarrantyUserNeighborhoodNames,
  getNeighborhoodWarrantyUsers,
  addWarrantyUserToNeighborhood,
  deleteWarrantyUserFromNeighborhood
} from '../../../../fetch';

export const WarrantySupervisors = ({ neighborhoodId }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [selectedWarrantyUserId, setSelectedWarrantyUserId] = useState<string | number>('');
  const [selectedWarrantyUserName, setSelectedWarrantyUserName] = useState<string>('');
  const [warrantyUserPriorityOrder, setWarrantyUserPriorityOrder] = useState<string | number>('');
  const [isDeleting, setDeleting] = useState<{ [id: number]: boolean }>({});

  const classes = useStyles();

  const formatDetails = (details: string[]) => {
    let neighborhoods = details.join(', ');

    return (
      <>
        {neighborhoods.length !== 0 ? (
          <Alert severity="info">
            <AlertTitle className={classes.alertTitle}>
              Warranty {selectedWarrantyUserName} is currently assigned to:
            </AlertTitle>
            {neighborhoods}
          </Alert>
        ) : (
          <Alert severity="info">
            <AlertTitle className={classes.alertTitle}>
              Warranty {selectedWarrantyUserName} is not assigned to any neighborhoods.
            </AlertTitle>
          </Alert>
        )}
      </>
    );
  };

  const {
    isLoading: isLoadingAvailableWarrantyUsers,
    data: availableWarrantyUsers,
    refetch: refetchAvailableWarrantyUsers
  } = useQuery<IDropdownResponse[], Error>(
    ['getAvailableWarrantyUsers'],
    () => getAvailableWarrantyUsers(),
    {
      notifyOnChangeProps: 'tracked'
    }
  );

  const { isLoading: isLoadingWarrantyUserNeighborhoodNames, data: warrantyUserNeighborhoodNames } =
    useQuery<string[], Error>(
      ['getWarrantyUserNeighborhoodNames', selectedWarrantyUserId],
      () => getWarrantyUserNeighborhoodNames(selectedWarrantyUserId),
      {
        notifyOnChangeProps: 'tracked',
        enabled: !!selectedWarrantyUserId
      }
    );

  const {
    isLoading: isLoadingNeighborhoodWarrantyUsers,
    data: neighborhoodsWarrantyUsers,
    refetch: refetchNeighborhoodWarrantyUsers
  } = useQuery<IAvailableNeighborhoodsWarrantyUser[], Error>(
    ['getNeighborhoodWarrantyUsers', neighborhoodId],
    () => getNeighborhoodWarrantyUsers(neighborhoodId),
    {
      notifyOnChangeProps: 'tracked',
      enabled: !!neighborhoodId
    }
  );

  const handleDelete = async (warrantyUserId: number | string) => {
    const result = window.confirm('Are you sure you want to delete this Warranty?');
    if (result) {
      setDeleting({ [neighborhoodId]: true });
      try {
        await deleteWarrantyUserFromNeighborhood(warrantyUserId, neighborhoodId);
        refetchNeighborhoodWarrantyUsers();
        enqueueSnackbar('Warranty removed!', {
          variant: 'success'
        });
      } catch (error) {
        enqueueSnackbar(error?.Detail ?? 'Error deleting warranty.', {
          variant: 'error'
        });
      } finally {
        setDeleting({ [neighborhoodId]: false });
      }
    }
  };

  const columns = useMemo(() => {
    return [
      {
        Header: 'Name',
        accessor: 'name',
        sort: false
      },
      {
        Header: 'Priority',
        accessor: 'priorityOrder',
        sort: false
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        overrideWidth: 200,
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IAvailableNeighborhoodsWarrantyUser } };
        }) => {
          return (
            <>
              <LoadingButton
                loading={isDeleting[original.warrantyUserId]}
                loadingPosition="start"
                color="error"
                disabled={isDeleting[original.warrantyUserId]}
                startIcon={<DeleteForever />}
                onClick={() => {
                  handleDelete(original.warrantyUserId);
                }}
              >
                Delete
              </LoadingButton>
            </>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Card title="Warranty" icon={<SupervisedUserCircle />}>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={6}>
          <SMBSelect
            variant="outlined"
            disabled={isLoadingAvailableWarrantyUsers}
            name={`warranty`}
            label={`Warranty`}
            id={`warranty`}
            value={selectedWarrantyUserId}
            size="small"
            handleClear={() => setSelectedWarrantyUserId('')}
            onChange={e => {
              setSelectedWarrantyUserId(e.target.value);
            }}
          >
            {!isLoadingAvailableWarrantyUsers && availableWarrantyUsers?.length === 0 && (
              <MenuItem disabled>No available users</MenuItem>
            )}
            {!isLoadingAvailableWarrantyUsers &&
              availableWarrantyUsers?.map((record, index) => {
                return (
                  <MenuItem
                    key={`${index}`}
                    value={record.value}
                    onClick={() => {
                      setSelectedWarrantyUserName(record.description);
                    }}
                  >
                    {record.description}
                  </MenuItem>
                );
              })}
          </SMBSelect>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            type="number"
            fullWidth
            size="small"
            autoComplete="nope"
            label="Priority Order"
            name="priorityOrder"
            value={warrantyUserPriorityOrder}
            onChange={e => {
              setWarrantyUserPriorityOrder(e.target.value);
            }}
          />
        </Grid>
      </Grid>
      <ButtonGroup>
        <Box mb={1} mt={1}>
          <Button
            className={classes.button}
            startIcon={<Add />}
            sx={{ minWidth: '14rem' }}
            color="primary"
            variant="contained"
            disabled={!selectedWarrantyUserId || !warrantyUserPriorityOrder}
            onClick={async () => {
              try {
                await addWarrantyUserToNeighborhood(
                  neighborhoodId,
                  selectedWarrantyUserId,
                  warrantyUserPriorityOrder
                );
                refetchAvailableWarrantyUsers();
                refetchNeighborhoodWarrantyUsers();
                setSelectedWarrantyUserId('');
                setWarrantyUserPriorityOrder('');
                enqueueSnackbar('Warranty added!', {
                  variant: 'success'
                });
              } catch (error) {
                enqueueSnackbar(error?.Detail ?? 'Error adding Warranty.', {
                  variant: 'error'
                });
              }
            }}
          >
            Add
          </Button>
          <Button
            className={classes.button}
            startIcon={<Close />}
            sx={{ minWidth: '14rem' }}
            color="inherit"
            variant="contained"
            disabled={!selectedWarrantyUserId}
            onClick={() => {
              setSelectedWarrantyUserId('');
              setWarrantyUserPriorityOrder('');
            }}
          >
            Cancel
          </Button>
        </Box>
      </ButtonGroup>
      {warrantyUserNeighborhoodNames && !isLoadingWarrantyUserNeighborhoodNames && (
        <Typography className={classes.roleDetails}>
          {formatDetails(warrantyUserNeighborhoodNames)}
        </Typography>
      )}
      <Divider />
      <Grid item xs={12}>
        <Table
          columns={columns}
          data={neighborhoodsWarrantyUsers ?? []}
          hidePagination
          isLoading={isLoadingNeighborhoodWarrantyUsers}
        />
      </Grid>
    </Card>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    padding: theme.spacing(2)
  },
  button: {
    whiteSpace: 'nowrap'
  },
  buttonGroup: {
    marginTop: theme.spacing(1)
  },
  roleDetails: {
    paddingBottom: theme.spacing(1)
  },
  selectWrapper: {
    width: '50%',
    marginBottom: '12px',
    '@media (max-width: 768px)': {
      width: '100%'
    }
  },
  textfieldWrapper: {
    width: '50%',
    '@media (max-width: 768px)': {
      width: '100%'
    }
  },
  alertTitle: {
    fontWeight: 'bold'
  }
}));
